import { useMemo } from "react";
import { Box, Typography, Button, Paper, Divider } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Short, stable-per-render reference ID — helps IT/support correlate a report
// against this specific denied attempt without needing server-side logging.
const makeTraceId = () =>
    `REQ-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export default function InvalidRequestPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, selectedCompany } = useAuth() || {};
    const traceId = useMemo(makeTraceId, [location.pathname]);
    const timestamp = useMemo( () => new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }), [location.pathname] );

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "70vh",
                px: 2,
                py: 4,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Soft background accents */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10%",
                    right: "-8%",
                    width: 420,
                    height: 420,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(220,38,38,0.06) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: "-12%",
                    left: "-6%",
                    width: 360,
                    height: 360,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(24,119,242,0.05) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />

            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: 560,
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
                    position: "relative",
                }}
            >
                {/* Status strip */}
                <Box sx={{ height: 4, background: "linear-gradient(90deg, #dc2626 0%, #f97316 100%)" }} />

                <Box sx={{ px: { xs: 3, sm: 5 }, pt: { xs: 3, sm: 3.5 }, pb: { xs: 4, sm: 5 } }}>
                    {/* Brand header */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                        <Box component="img" src="/web01.svg" alt="ReonBook" sx={{ height: 32, objectFit: "contain" }} />
                    </Box>

                    {/* Header row: icon + status code */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 2,
                                bgcolor: "#fef2f2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <BlockIcon sx={{ fontSize: 26, color: "#dc2626" }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    color: "#dc2626",
                                    textTransform: "uppercase",
                                }}
                            >
                                Error 404 · Page Not Found
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                Invalid Path
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 3 }}>
                        The page you requested is not part of this application's active
                        menu and cannot be opened directly. If you believe this is an
                        error, contact your system administrator.
                    </Typography>

                    <Divider sx={{ mb: 2.5 }} />

                    {/* Technical / audit detail panel */}
                    <Box
                        sx={{
                            bgcolor: "#f8fafc",
                            border: "1px solid rgba(0,0,0,0.06)",
                            borderRadius: 1.5,
                            px: 2,
                            py: 1.5,
                            mb: 3.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {[
                            ["Requested Path", location.pathname],
                            ["User", user?.EmployeeName || user?.UserName || "—"],
                            ["Company", selectedCompany?.CompanyName || "—"],
                            ["Timestamp", timestamp],
                            ["Reference ID", traceId],
                        ].map(([label, value]) => (
                            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Consolas', 'Courier New', monospace",
                                        fontSize: "0.78rem",
                                        color: "#334155",
                                        wordBreak: "break-all",
                                        textAlign: "right",
                                    }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            startIcon={<HomeOutlinedIcon />}
                            onClick={() => navigate("/main/home")}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 1.5,
                                px: 2.5,
                                bgcolor: "#1877F2",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#1464cc", boxShadow: "none" },
                            }}
                        >
                            Go to Dashboard
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackOutlinedIcon />}
                            onClick={() => navigate(-1)}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 1.5,
                                px: 2.5,
                                borderColor: "rgba(0,0,0,0.15)",
                                color: "#334155",
                                "&:hover": { borderColor: "#1877F2", bgcolor: "rgba(24,119,242,0.04)" },
                            }}
                        >
                            Go Back
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
